'use client';
/**
 * Absences & congés (lot 2) — deux écrans, deux publics.
 *
 * · « Mes congés » s'adresse à TOUT le personnel : déposer une demande ne
 *   dépend pas du rôle `rh`. En faire un privilège aurait vidé le circuit
 *   mixte (D4) de son sens — le salarié demande, le gestionnaire décide.
 * · « Absences & congés » est la vue du gestionnaire RH : demandes à traiter,
 *   saisie pour autrui avec justificatif, soldes.
 *
 * ⚠️ Cet écran ne cache RIEN de sensible par lui-même : le serveur a déjà
 * rédigé ce qu'il envoie (`redigerAbsence`). Ce que l'interface n'affiche pas,
 * elle ne l'a jamais reçu.
 */
import { refusDepot } from '@/lib/depot';
import React from 'react';
import { useGed } from '@/lib/store';
import type { RhAbsence, RhPersonnel } from '@/lib/types';
import { BoutonCreer, I, TitrePage, card, inputStyle, EmptyState, ConfirmButton, useEscape, Help, FieldError, FilterBar, Skeleton, Selecteur, Modale as ModaleCommune, useConfirmation } from '@/components/ui';
import { Tableau } from '@/shared/Tableau';
// Le panneau « dire ce que le geste emporte », partagé avec les registres qualité.
import { SuppressionRaisonnee } from '@/components/SuppressionRaisonnee';
import { MOTIF_DEROGATION_MIN, jourIso, isoJour, fmtDate } from '@/lib/meta';
/* ⚠️ LE vocabulaire des états d'absence, partagé (05/10/2026) : la copie locale
   de cet écran laissait « Mon planning » employer celui des documents, qui
   écrivait « Brouillon » sur un congé validé. */
import { etatAbsence } from '@/lib/etats-absence';
import { nomDe } from '@/lib/nom';
import { libelleJourLong } from '@/lib/jour';

import { Bouton } from '@/shared/Bouton';

const fmt = (iso: string) => fmtDate(iso);

/* « Ce jour est-il inclus ? » (02/11/2026) — la question revenait à chaque
   saisie. La fin est le DERNIER JOUR D'ABSENCE, inclus ; on l'écrit, et on
   donne le jour de reprise. ⚠️ Le laboratoire travaille week-ends et fériés :
   la reprise est le LENDEMAIN, et le planning de la personne dit si ce jour
   est travaillé — on ne saute ni dimanche ni férié à sa place. */
function RepriseConge({ debut, fin, demiFin, vous }: { debut: string; fin: string; demiFin?: boolean; vous?: boolean }) {
  if (!debut || !fin || fin < debut) return null;
  const d = new Date(`${fin}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + 1);
  return (
    <p style={{ fontSize: 12.5, margin: '8px 0 0', lineHeight: 1.5 }}>
      Absence {debut === fin ? <>le {libelleJourLong(debut)}</> : <>du {libelleJourLong(debut)} au {libelleJourLong(fin)}</>}, <strong>inclus</strong>.
      {' '}{demiFin
        ? <>Reprise le <strong>{libelleJourLong(fin)}</strong>, après la demi-journée.</>
        : <>Reprise le <strong>{libelleJourLong(isoJour(d))}</strong>
          <span style={{ color: 'var(--text-muted)' }}> (ou le premier jour travaillé qui suit dans {vous ? 'votre' : 'son'} planning)</span>.</>}
    </p>
  );
}

function Statut({ s }: { s: string }) {
  const v = etatAbsence(s);
  return (
    <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 9px', background: v.fond, color: v.texte, whiteSpace: 'nowrap' }}>
      {v.label}
    </span>
  );
}

/* ========================= MES CONGÉS (tout salarié) ========================= */

export function MesConges() {
  const { mesAbsences, rhAcces, demanderAbsence, deciderAbsence } = useGed();
  const [ouvert, setOuvert] = React.useState(false);
  const motifs = rhAcces?.motifsDemandables || [];

  /* MON SOLDE de l'année : droit, jours pris, jours restants. Calculé par le
     serveur (même fonction que l'écran RH). Rechargé quand mes absences
     changent : une demande validée ou annulée déplace le solde. `null` tant
     qu'on ne sait pas — rien n'est affiché plutôt qu'un faux « 0 ». */
  const annee = new Date().getFullYear();
  const [solde, setSolde] = React.useState<{ droit: number | null; report: number; pris: number; solde: number | null; incomplet: boolean } | null>(null);
  React.useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/rh/mon-solde?annee=${annee}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivant) setSolde(d?.solde ?? null); })
      .catch(() => { if (vivant) setSolde(null); });
    return () => { vivant = false; };
  }, [annee, mesAbsences]);

  /* L'ouverture automatique du formulaire a été RETIRÉE avec l'entrée
     « Demander un congé » du menu du profil (14/08/2026) : plus personne ne
     posait le drapeau, et un mécanisme qui ne se déclenche jamais se lit
     pourtant comme du code vivant — on le maintient, on le teste, on hésite à
     y toucher. */

  return (
    <section>
      <TitrePage eyebrow={<>Plannings &amp; RH</>} titre="Mes congés"
        sousTitre={<>
          Vos demandes et vos absences. Une maladie ou une maternité ne se déclare pas ici : elle est
          enregistrée par le gestionnaire RH, avec la pièce justificative.
        </>} />

      {solde && (
        <div role="group" aria-label={`Mon solde de congés ${annee}`}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, margin: '14px 0' }}>
          {[
            { l: `Solde total ${annee}`, v: solde.incomplet || solde.droit == null ? '—' : solde.droit + solde.report, aide: solde.report ? `dont ${solde.report} j de report` : 'droit annuel' },
            { l: 'Jours pris', v: solde.pris, aide: 'congés validés décomptés' },
            { l: 'Jours restants', v: solde.incomplet || solde.solde == null ? '—' : solde.solde, fort: true, neg: !solde.incomplet && solde.solde != null && solde.solde < 0, aide: 'solde − jours pris' },
          ].map((c) => (
            <div key={c.l} style={{ ...card, padding: '12px 14px' }}>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 600 }}>{c.l}</div>
              <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2, color: c.neg ? 'var(--red-600)' : c.fort ? 'var(--color-primary)' : 'var(--text-body)' }}>
                {c.v}{typeof c.v === 'number' && <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}> j</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{c.aide}</div>
            </div>
          ))}
          {solde.incomplet && (
            <p style={{ gridColumn: '1 / -1', margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
              Votre droit à congé n’est pas encore paramétré : le gestionnaire RH doit le renseigner.
            </p>
          )}
        </div>
      )}

      <BoutonCreer style={{ margin: '14px 0' }} onClick={() => setOuvert(true)} disabled={!motifs.length}>
        Demander un congé
      </BoutonCreer>
      {!motifs.length && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Aucun motif n’est encore ouvert à la demande — le gestionnaire RH doit paramétrer les motifs d’absence.
        </p>
      )}

      {!mesAbsences.length ? (
        <EmptyState titre="Aucune absence enregistrée" indice="Vos demandes de congé apparaîtront ici, avec leur suite." />
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {mesAbsences.map((a) => (
            <div key={a.documentId} style={{ ...card, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <strong style={{ fontSize: 13.5 }}>{a.motifLabel}</strong>
                  <Statut s={a.statut} />
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
                  du {fmt(a.dateDebut)} au {fmt(a.dateFin)}
                  {a.joursDecomptes != null && <> · <strong>{a.joursDecomptes} jour(s)</strong> décompté(s)</>}
                </div>
                {a.statut === 'refusee' && a.motifDecision && (
                  <div style={{ fontSize: 12.5, color: 'var(--red-600)', marginTop: 4 }}>
                    Motif du refus : {a.motifDecision}
                  </div>
                )}
              </div>
              {a.statut === 'demandee' && (
                <ConfirmButton
                  label="Annuler" variant="outline"
                  question="Annuler cette demande ?"
                  confirmer="Annuler la demande"
                  onConfirm={() => deciderAbsence(a.documentId, 'annulee')}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {ouvert && (
        <FormulaireDemande
          motifs={motifs}
          onFermer={() => setOuvert(false)}
          onValider={async (d) => { const ok = await demanderAbsence(d); if (ok) setOuvert(false); }}
        />
      )}
    </section>
  );
}

function FormulaireDemande({ motifs, onFermer, onValider }: {
  motifs: { code: string; label: string; decompteSolde: boolean }[];
  onFermer: () => void;
  onValider: (d: { motif: string; dateDebut: string; dateFin: string; demiJourneeDebut?: boolean; demiJourneeFin?: boolean; heureDebut?: string; heureFin?: string }) => Promise<void>;
}) {
  const [motif, setMotif] = React.useState(motifs[0]?.code || '');
  const [debut, setDebut] = React.useState('');
  const [fin, setFin] = React.useState('');
  const [dj, setDj] = React.useState(false);
  const [df, setDf] = React.useState(false);
  /* L'autorisation de sortie HORAIRE (26/08/2026) : deux heures sur une seule
     journée. Le formulaire bascule tout seul — un seul jour, des heures à la
     place des demi-journées — plutôt que d'offrir des cases qui se contredisent. */
  const [hDebut, setHDebut] = React.useState('');
  const [hFin, setHFin] = React.useState('');
  const horaire = motif === 'autorisation_sortie';
  useEscape(true, onFermer);
  const incoherent = !!debut && !!fin && fin < debut;
  const heuresIncoherentes = horaire && !!hDebut && !!hFin && hFin <= hDebut;

  return (
    <Modale titre="Demander un congé" onFermer={onFermer}>
      <label style={lbl}>Motif
        <select style={{ ...inputStyle, width: '100%' }} value={motif} onChange={(e) => setMotif(e.target.value)}>
          {motifs.map((m) => <option key={m.code} value={m.code}>{m.label}{m.decompteSolde ? ' (décompté du solde)' : ''}</option>)}
        </select>
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
        <label style={lbl}>{horaire ? 'Le' : 'Du'}
          <input type="date" style={{ ...inputStyle, width: '100%' }} value={debut}
            onChange={(e) => { setDebut(e.target.value); if (!fin || horaire) setFin(e.target.value); }} />
        </label>
        {!horaire && (
          <label style={lbl}>Au (dernier jour d’absence, inclus)
            <input type="date" style={{ ...inputStyle, width: '100%' }} value={fin} onChange={(e) => setFin(e.target.value)} />
          </label>
        )}
        {horaire && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={lbl}>Sortie à
              <input type="time" style={{ ...inputStyle, width: '100%' }} value={hDebut} onChange={(e) => setHDebut(e.target.value)} />
            </label>
            <label style={lbl}>Retour à
              <input type="time" style={{ ...inputStyle, width: '100%' }} value={hFin} onChange={(e) => setHFin(e.target.value)} />
            </label>
          </div>
        )}
      </div>
      {incoherent && <FieldError>La date de fin précède la date de début.</FieldError>}
      {!horaire && <RepriseConge debut={debut} fin={fin} demiFin={df} vous />}
      {heuresIncoherentes && <FieldError>L’heure de retour doit suivre l’heure de sortie.</FieldError>}
      {!horaire && (
        <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12.5 }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={dj} onChange={(e) => setDj(e.target.checked)} /> Demi-journée le premier jour
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={df} onChange={(e) => setDf(e.target.checked)} /> Demi-journée le dernier jour
          </label>
        </div>
      )}
      {horaire && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 10 }}>
          Une sortie horaire ne touche pas au solde de congés et ne marque pas la journée absente :
          le pointage du jour reste le vôtre, moins la sortie.
        </p>
      )}
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 10 }}>
        Le nombre de jours décomptés est calculé par le laboratoire (jours ouvrables ou ouvrés, jours
        fériés déduits) — il s’affichera sur la demande une fois déposée.
      </p>
      <Actions onFermer={onFermer}
        valide={!!motif && !!debut && !!fin && !incoherent && (!horaire || (!!hDebut && !!hFin && !heuresIncoherentes))}
        onValider={() => onValider(horaire
          ? { motif, dateDebut: debut, dateFin: debut, heureDebut: hDebut, heureFin: hFin }
          : { motif, dateDebut: debut, dateFin: fin, demiJourneeDebut: dj, demiJourneeFin: df })}
        libelle="Déposer la demande" />
    </Modale>
  );
}

/* ================== ABSENCES & CONGÉS (gestionnaire RH) ================== */

/** Les trois onglets d'« Absences & congés » (RQ, 30/09/2026 : « soldes par
 *  année, demandes de congés, historique dans un tableau comme mon registre »). */
type OngletAbsences = 'soldes' | 'demandes' | 'historique';
const MOIS_COURTS = ['janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'];
const moisDe = (iso: string) => (iso ? `${MOIS_COURTS[Number(iso.slice(5, 7)) - 1]}-${iso.slice(2, 4)}` : '—');

export function Absences() {
  const { rhAcces, rhAbsences, rhSoldes, rhPersonnel, rhParametres, deciderAbsence, demanderAbsence, rhChargement, loadRhGestion, rhAnneeGestion, session } = useGed();
  const [saisie, setSaisie] = React.useState(false);
  const [refus, setRefus] = React.useState<RhAbsence | null>(null);
  const [onglet, setOnglet] = React.useState<OngletAbsences>(() => {
    try { return (localStorage.getItem('bio24.absences.onglet') as OngletAbsences) || 'demandes'; } catch { return 'demandes'; }
  });
  const choisir = (o: OngletAbsences) => { setOnglet(o); try { localStorage.setItem('bio24.absences.onglet', o); } catch { /* sans stockage */ } };
  const annee = rhAnneeGestion || new Date().getFullYear();
  // Une seule recherche pour l'historique ET les soldes : les deux listes
  // parlent des mêmes personnes, et le gestionnaire cherche « quelqu'un »,
  // pas « une ligne d'un tableau ».
  const [q, setQ] = React.useState('');
  if (!rhAcces?.acces) return <AccesRefuseRh />;

  const enAttente = rhAbsences.filter((a) => a.statut === 'demandee');
  const incomplets = rhSoldes.filter((s) => s.incomplet).length;

  const qq = q.trim().toLowerCase();
  const correspond = (nom: string, extra = '') => `${nom} ${extra}`.toLowerCase().includes(qq);
  const soldesVus = qq ? rhSoldes.filter((s) => correspond(s.nom)) : rhSoldes;

  const nomOrg = (orgId?: string | null) => {
    if (!orgId) return '';
    const p = rhPersonnel.find((x) => x.orgId === orgId);
    return p ? nomDe(p) : orgId;
  };
  const changerAnnee = (n: number) => { void loadRhGestion(annee + n); };

  const ONGLETS: { k: OngletAbsences; label: string }[] = [
    { k: 'soldes', label: `Soldes des congés ${annee}` },
    { k: 'demandes', label: `Demandes de congés${enAttente.length ? ` (${enAttente.length})` : ''}` },
    { k: 'historique', label: 'Historique' },
  ];

  return (
    <section>
      <TitrePage eyebrow={<>Plannings &amp; RH</>} titre={<>Absences &amp; congés</>}
        sousTitre={<>
          Vous êtes la seule fonction à voir les motifs détaillés et les certificats. Les autres
          utilisateurs voient « absent » et les dates — c’est tout ce dont le planning a besoin.
        </>} />

      {incomplets > 0 && (
        <div style={{ ...card, padding: 20, background: 'var(--amber-50)', border: '1px solid var(--amber-500)', marginTop: 14 }}>
          <strong style={{ fontSize: 13, color: 'var(--amber-600)' }}>Le droit à congé n’est pas paramétré</strong>
          <p style={{ fontSize: 12.5, margin: '4px 0 0' }}>
            {incomplets} salarié(s) sans droit calculable : les soldes affichés ne veulent encore rien
            dire. Renseignez le droit annuel dans <strong>Paramétrage RH</strong>.
          </p>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0', flexWrap: 'wrap' }}>
        <BoutonCreer onClick={() => setSaisie(true)}>Enregistrer une absence</BoutonCreer>
        <span style={{ flex: 1 }} />
        <Bouton taille="sm" aria-label="Année précédente" onClick={() => changerAnnee(-1)}><I n="chevron-left" s={14} /></Bouton>
        <strong style={{ fontSize: 14, minWidth: 48, textAlign: 'center' }}>{annee}</strong>
        <Bouton taille="sm" aria-label="Année suivante" onClick={() => changerAnnee(1)}><I n="chevron-right" s={14} /></Bouton>
      </div>

      <div role="tablist" aria-label="Rubriques des absences" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-default)', marginBottom: 12, flexWrap: 'wrap' }}>
        {ONGLETS.map((o) => (
          <button key={o.k} role="tab" aria-selected={onglet === o.k} onClick={() => choisir(o.k)}
            style={{
              border: 'none', background: 'none', cursor: 'pointer', padding: '8px 14px', fontSize: 13.5,
              fontWeight: onglet === o.k ? 700 : 500, color: onglet === o.k ? 'var(--color-primary-active)' : 'var(--text-muted)',
              borderBottom: `2px solid ${onglet === o.k ? 'var(--color-primary-active)' : 'transparent'}`, marginBottom: -1,
            }}>
            {o.label}
          </button>
        ))}
      </div>

      {!rhAbsences.length && !rhSoldes.length && rhChargement > 0 ? (
        <div style={{ ...card, padding: 0, overflow: 'hidden', marginTop: 14 }}><Skeleton rows={5} /></div>
      ) : onglet === 'demandes' ? (
      <>
      <h2 style={h2}>Demandes à traiter {enAttente.length > 0 && <span style={{ color: 'var(--amber-600)' }}>({enAttente.length})</span>}</h2>
      {!enAttente.length ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>Aucune demande en attente.</p>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {enAttente.map((a) => (
            <div key={a.documentId} style={{ ...card, padding: '14px 16px', display: 'grid', gap: 10, borderInlineStart: `3px solid ${a.concomitanceSensible ? 'var(--red-500)' : 'var(--amber-500)'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <LigneAbsence a={a} />
                <ConfirmButton label="Valider" variant="primary" question={`Valider ${a.joursDecomptes ?? '?'} jour(s) pour ${nomDe(a.personnel)} ?`}
                  confirmer="Valider"
                  /* ⚠️ LE REMPLAÇANT EST TRANSFÉRÉ QUAND LA DEMANDE EN DÉSIGNE UN.
                     La personne l'a nommé dans sa demande (ENR-H1-01) ; valider
                     sans reprendre le planning laisserait des postes au nom de
                     quelqu'un qui n'est pas là. Le serveur revérifie les
                     habilitations du remplaçant et refuse ce qu'il ne peut pas
                     tenir — le refus est dit, la case reste en conflit. */
                  onConfirm={() => deciderAbsence(a.documentId, 'validee', undefined, !!a.remplacantOrgId)} />
                <Bouton variante="danger" taille="sm" onClick={() => setRefus(a)}>Refuser</Bouton>
              </div>
              <Concomitances a={a} />
            </div>
          ))}
        </div>
      )}
      </>
      ) : onglet === 'soldes' ? (
      <>
      <FilterBar q={q} onQ={setQ} placeholder="Rechercher un salarié…" total={rhSoldes.length} affiches={soldesVus.length} onReset={() => setQ('')} />
      <div style={{ ...card, padding: 0 }}>
        <Tableau
          defilementHorizontal
          id="rh-soldes-conges"
          /* L5 (10/10/2026) : niveau STANDARD — filtres, masquage, export de
             référence (les quatre soldes en nombres). */
          niveau="standard"
          vide={{ titre: rhSoldes.length ? 'Aucun salarié ne correspond.' : 'Aucun solde à afficher.' }}
          export={{ registre: 'soldes-conges', libelleRegistre: 'Soldes de congés', auteur: session.name || session.key, filtres: q.trim() ? [`Recherche « ${q.trim()} »`] : [] }}
          lignes={soldesVus}
          cleLigne={(x) => x.orgId}
          colonnes={[
            {
              cle: 'nom', libelle: 'Salarié', valeur: (x) => x.nom, epinglee: true, obligatoire: true, export: true,
              style: { textAlign: 'start' },
              rendu: (x) => (
                <>
                  {x.nom}
                  <span style={{ display: 'block', fontSize: 12.5, color: x.incomplet ? 'var(--amber-600)' : 'var(--text-faint)' }}>{x.explication}</span>
                </>
              ),
            },
            /* Les quatre colonnes chiffrées se trient sur le NOMBRE, jamais sur
               le « — » affiché quand le dossier est incomplet : un tri textuel
               aurait rangé les tirets entre 1 et 2. Un dossier incomplet vaut
               `null` et se range en queue, ce qui est la lecture utile — on
               cherche les soldes, pas les trous. */
            {
              cle: 'droit', libelle: 'Droit', valeur: (x) => (x.incomplet ? null : x.droit), type: 'nombre', export: true,
              rendu: (x) => (x.incomplet ? '—' : x.droit),
            },
            {
              cle: 'report', libelle: 'Report', valeur: (x) => x.report || null, type: 'nombre', export: true,
              rendu: (x) => x.report || '—',
            },
            {
              cle: 'pris', libelle: 'Pris', valeur: (x) => x.pris || null, type: 'nombre', export: true,
              rendu: (x) => x.pris || '—',
            },
            {
              /* LE SOLDE — la colonne qu'on vient trier. Un tri croissant fait
                 remonter les soldes NÉGATIFS : ce sont eux qui appellent une
                 décision. */
              cle: 'solde', libelle: 'Solde', valeur: (x) => (x.incomplet ? null : x.solde), type: 'nombre', export: true,
              style: { fontWeight: 700 },
              rendu: (x) => (
                <span style={{ color: x.incomplet ? 'var(--text-faint)' : x.solde < 0 ? 'var(--red-600)' : 'var(--text-body)' }}>
                  {x.incomplet ? '—' : x.solde}
                </span>
              ),
            },
          ]}
        />
      </div>
      </>
      ) : (
        <HistoriqueConges key={annee} annee={annee} nomOrg={nomOrg} />
      )}

      {saisie && (
        <FormulaireSaisie
          personnel={rhPersonnel.filter((p) => p.type === 'salarie' && p.statut !== 'sorti')}
          motifs={rhParametres?.motifsAbsence || []}
          onFermer={() => setSaisie(false)}
          onValider={async (d) => { const ok = await demanderAbsence(d); if (ok) setSaisie(false); }}
        />
      )}
      {refus && (
        <FormulaireRefus a={refus} onFermer={() => setRefus(null)}
          onValider={async (m) => { const ok = await deciderAbsence(refus.documentId, 'refusee', m); if (ok) setRefus(null); }} />
      )}
    </section>
  );
}

/**
 * L'HISTORIQUE DES CONGÉS (RQ, 30/09/2026) — « juste l'historique », sur la
 * période qu'on choisit, en tableau comme le registre papier ; un clic sur une
 * ligne ouvre tout le détail. Les demandes en attente n'y sont pas : elles ont
 * leur onglet.
 *
 * Le SOLDE AVANT / APRÈS se calcule ici, dans l'ordre des dates : droit de
 * l'année (solde d'ouverture déclaré, ou calculé) + report, moins chaque congé
 * validé qui se décompte. Un dossier sans droit connu n'a pas de solde : « — ».
 */
function HistoriqueConges({ annee, nomOrg }: { annee: number; nomOrg: (o?: string | null) => string }) {
  const rhAbsences = useGed((s) => s.rhAbsences);
  const session = useGed((s) => s.session);
  const [du, setDu] = React.useState(`${annee}-01-01`);
  const [au, setAu] = React.useState(`${annee}-12-31`);
  const [lignes, setLignes] = React.useState<RhAbsence[] | null>(null);
  const [soldesAn, setSoldesAn] = React.useState<Record<number, { orgId: string; droit: number; report: number; incomplet: boolean }[]>>({});
  const [q, setQ] = React.useState('');
  const [ouverte, setOuverte] = React.useState<RhAbsence | null>(null);
  React.useEffect(() => {
    if (!du || !au || au < du) return;
    let vivant = true;
    const annees: number[] = [];
    for (let y = Number(du.slice(0, 4)); y <= Number(au.slice(0, 4)); y += 1) annees.push(y);
    void Promise.all([
      fetch(`/api/ged/ged/rh/absences?du=${du}&au=${au}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : { absences: [] })),
      ...annees.map((y) => fetch(`/api/ged/ged/rh/compteurs?annee=${y}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : { soldes: [] }))),
    ]).then(([abs, ...cpts]) => {
      if (!vivant) return;
      setLignes(((abs.absences || []) as RhAbsence[]).filter((a) => a.statut !== 'demandee'));
      setSoldesAn(Object.fromEntries(annees.map((y, i) => [y, cpts[i]?.soldes || []])));
    });
    return () => { vivant = false; };
    /* `rhAbsences` : une décision prise ailleurs (annulation) relit la période. */
  }, [du, au, rhAbsences]);

  const qq = q.trim().toLowerCase();
  const vues = (lignes || [])
    .filter((a) => !qq || `${nomDe(a.personnel)} ${a.motifLabel} ${nomOrg(a.remplacantOrgId)}`.toLowerCase().includes(qq))
    .sort((x, y) => x.dateDebut.localeCompare(y.dateDebut) || String(x.dateDemande || '').localeCompare(String(y.dateDemande || '')));

  /* Les soldes se déroulent sur TOUTE l'année de chaque congé, pas sur la seule
     fenêtre affichée : sinon un filtre sur juin repartirait du droit plein. */
  const soldes = React.useMemo(() => {
    const m = new Map<string, { avant: number; apres: number }>();
    const courant = new Map<string, number | null>();
    for (const a of [...(lignes || [])].filter((x) => x.statut === 'validee' && x.decompteSolde)
      .sort((x, y) => x.dateDebut.localeCompare(y.dateDebut))) {
      const y = Number(a.dateDebut.slice(0, 4));
      const k = `${y}|${a.personnel?.orgId || ''}`;
      if (!courant.has(k)) {
        const s = (soldesAn[y] || []).find((x) => x.orgId === a.personnel?.orgId);
        courant.set(k, s && !s.incomplet ? s.droit + (s.report || 0) : null);
      }
      const avant = courant.get(k);
      if (avant == null) continue;
      const apres = Math.round((avant - Number(a.joursDecomptes || 0)) * 100) / 100;
      m.set(a.documentId, { avant, apres });
      courant.set(k, apres);
    }
    return m;
  }, [lignes, soldesAn]);
  const debutPartiel = du.slice(5) !== '01-01';

  return (
    <>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', margin: '4px 0 10px' }}>
        <label style={{ fontSize: 12.5 }}>Du<br /><input type="date" value={du} max={au} onChange={(e) => setDu(e.target.value)} style={{ ...inputStyle, width: 160 }} /></label>
        <label style={{ fontSize: 12.5 }}>Au<br /><input type="date" value={au} min={du} onChange={(e) => setAu(e.target.value)} style={{ ...inputStyle, width: 160 }} /></label>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un salarié, un motif, un remplaçant…"
          aria-label="Rechercher dans l’historique" style={{ ...inputStyle, flex: 1, minWidth: 220 }} />
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{lignes ? `${vues.length} absence(s)` : ''}</span>
      </div>
      {debutPartiel && (
        <p style={{ fontSize: 11.5, color: 'var(--text-faint)', margin: '0 0 8px' }}>
          Les soldes tiennent compte des congés antérieurs à la période seulement s’ils y figurent : pour un solde exact, partez du 1er janvier.
        </p>
      )}
      {(
        <div style={{ ...card, padding: 0 }}>
          <Tableau
            defilementHorizontal
            id="rh-historique-conges"
            ajusterLargeur
            /* L5 (10/10/2026) : niveau STANDARD — dates filtrables du/au, motif
               et statut en liste, export de référence, états intégrés. Les
               dates passent par `fmtDate` (É-1) : « 05/10/2026 », plus « 05/10 ». */
            niveau="standard"
            chargement={!lignes}
            vide={{ titre: (lignes || []).length ? 'Aucune absence ne correspond à cette recherche.' : 'Aucune absence enregistrée sur cette période.' }}
            export={{ registre: 'historique-conges', libelleRegistre: 'Historique des congés', auteur: session.name || session.key, filtres: q.trim() ? [`Recherche « ${q.trim()} »`] : [] }}
            lignes={vues}
            cleLigne={(a) => a.documentId}
            ligneProps={(a) => ({
              onClick: () => setOuverte(a), style: { cursor: 'pointer' }, tabIndex: 0,
              onKeyDown: (e) => { if (e.key === 'Enter') setOuverte(a); }, title: 'Voir le détail',
            })}
            colonnes={[
              { cle: 'mois', libelle: 'Mois', valeur: (a) => a.dateDebut, export: true, exporter: (a) => moisDe(a.dateDebut), rendu: (a) => moisDe(a.dateDebut) },
              { cle: 'demande', libelle: 'Date demande', valeur: (a) => a.dateDemande || '', type: 'date', export: true, exporter: (a) => (a.dateDemande ? fmtDate(a.dateDemande) : ''), rendu: (a) => (a.dateDemande ? fmtDate(a.dateDemande) : '—') },
              { cle: 'personnel', libelle: 'Personnel', valeur: (a) => nomDe(a.personnel), type: 'liste', epinglee: true, obligatoire: true, export: true, style: { textAlign: 'start', fontWeight: 600 }, rendu: (a) => nomDe(a.personnel) || '—' },
              { cle: 'avant', libelle: 'Solde congé', valeur: (a) => soldes.get(a.documentId)?.avant ?? null, type: 'nombre', export: true, rendu: (a) => soldes.get(a.documentId)?.avant ?? '—' },
              { cle: 'jours', libelle: 'Nombre de jours', valeur: (a) => a.joursDecomptes ?? null, type: 'nombre', export: true, rendu: (a) => a.joursDecomptes ?? '—' },
              {
                cle: 'apres', libelle: 'Solde après', valeur: (a) => soldes.get(a.documentId)?.apres ?? null, type: 'nombre', export: true, style: { fontWeight: 700 },
                rendu: (a) => { const v = soldes.get(a.documentId)?.apres; return v == null ? '—' : <span style={{ color: v < 0 ? 'var(--red-600)' : undefined }}>{v}</span>; },
              },
              { cle: 'du', libelle: 'Date début', valeur: (a) => a.dateDebut, type: 'date', export: true, exporter: (a) => fmtDate(a.dateDebut), rendu: (a) => fmtDate(a.dateDebut) },
              { cle: 'au', libelle: 'Date fin', valeur: (a) => a.dateFin, type: 'date', export: true, exporter: (a) => fmtDate(a.dateFin), rendu: (a) => fmtDate(a.dateFin) },
              { cle: 'motif', libelle: 'Motif', valeur: (a) => a.motifLabel, type: 'liste', export: true, style: { textAlign: 'start' }, rendu: (a) => a.motifLabel },
              { cle: 'remplacant', libelle: 'Remplaçant', valeur: (a) => nomOrg(a.remplacantOrgId), type: 'liste', export: true, style: { textAlign: 'start' }, rendu: (a) => nomOrg(a.remplacantOrgId) || '—' },
              { cle: 'statut', libelle: 'Statut', valeur: (a) => a.statut, type: 'liste', libelleValeur: (v) => etatAbsence(v).label, export: true, exporter: (a) => etatAbsence(a.statut).label, rendu: (a) => <Statut s={a.statut} /> },
              { cle: 'valide', libelle: 'Validée le', valeur: (a) => (a.valideLe ? jourIso(a.valideLe) : ''), type: 'date', export: true, exporter: (a) => (a.valideLe ? fmtDate(jourIso(a.valideLe)) : ''), rendu: (a) => (a.valideLe ? fmtDate(jourIso(a.valideLe)) : '—') },
            ]}
          />
        </div>
      )}
      {ouverte && (
        <Modale titre={`${nomDe(ouverte.personnel)} — ${ouverte.motifLabel}`} sousTitre={<>du {fmt(ouverte.dateDebut)} au {fmt(ouverte.dateFin)}</>} onFermer={() => setOuverte(null)}>
          <DetailAbsence a={ouverte} solde={soldes.get(ouverte.documentId) || null} nomOrg={nomOrg} />
        </Modale>
      )}
    </>
  );
}

function L({ l, children }: { l: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 13 }}>
      <span style={{ color: 'var(--text-muted)' }}>{l}</span><span>{children}</span>
    </div>
  );
}

/** Tout ce qui se rattache à une absence — la fiche ouverte depuis l'historique. */
function DetailAbsence({ a, solde, nomOrg }: { a: RhAbsence; solde: { avant: number; apres: number } | null; nomOrg: (o?: string | null) => string }) {
  const vide = <span style={{ color: 'var(--text-faint)' }}>—</span>;
  const duree = a.heureDebut ? `sortie de ${a.heureDebut} à ${a.heureFin}`
    : `${a.joursDecomptes ?? '?'} jour(s)${a.demiJourneeDebut ? ' · commence l’après-midi' : ''}${a.demiJourneeFin ? ' · finit à midi' : ''}`;
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      <L l="Statut"><Statut s={a.statut} /></L>
      <L l="Motif">{a.motifLabel}</L>
      <L l="Période">du {fmt(a.dateDebut)} au {fmt(a.dateFin)}</L>
      <L l="Décompte">{duree}{a.decompteSolde ? ' · décompté du solde' : ' · hors solde'}</L>
      {solde && <L l="Solde de congés">{solde.avant} j avant → <strong>{solde.apres} j</strong> après</L>}
      <L l="Date de la demande">{a.dateDemande ? fmt(a.dateDemande) : vide}</L>
      <L l="Demandée par">{nomOrg(a.demandeur) || vide}</L>
      <L l="Remplaçant">{nomOrg(a.remplacantOrgId) || vide}</L>
      <L l="Décision">
        {a.valideLe ? <>le {fmt(jourIso(a.valideLe))}{a.validePar ? <> par {nomOrg(a.validePar)}</> : null}</> : vide}
        {a.motifDecision && <div style={{ color: a.statut === 'refusee' ? 'var(--red-600)' : 'var(--text-body)' }}>{a.motifDecision}</div>}
      </L>
      <L l="Commentaire">{a.commentaire || vide}</L>
      <L l="Justificatif">{a.aJustificatif ? <BoutonJustificatif a={a} /> : (a.justificatifNom || vide)}</L>
      {!!a.concomitantes?.length && <div style={{ marginTop: 8 }}><Concomitances a={a} /></div>}
      {a.statut === 'validee' && <div style={{ marginTop: 12 }}><AnnulerAbsence a={a} /></div>}
    </div>
  );
}

/** Ouvrir le justificatif d'une absence — c'est-à-dire, le plus souvent, un
 *  CERTIFICAT MÉDICAL.
 *
 *  Le lien n'a été posé que le 07/08/2026, et seulement une fois la trace
 *  écrite côté moteur : rendre une donnée de santé plus accessible sans
 *  pouvoir dire qui l'a consultée aurait été un recul, pas un service.
 *
 *  L'avertissement avant ouverture est celui de `BlocPieces` (dossiers
 *  d'intégration), mot pour mot dans son intention : on prévient AVANT, parce
 *  qu'après le clic la ligne est écrite et ne s'efface pas.
 *
 *  ⚠️ Le journal du laboratoire n'enregistre ni le nom du fichier ni le motif
 *  de l'absence — seulement qui a consulté, pour quelle personne, sur quelles
 *  dates, et quand. Le nom du fichier reste ici, à l'écran, sous les yeux du
 *  seul rôle RH qui a déjà accès à la pièce elle-même. */
/**
 * Annuler une absence VALIDÉE — correction d'une saisie erronée.
 *
 * Le point sensible est dit en premier : l'annulation RECRÉDITE les jours au
 * solde. Le compteur de congés se dérive des absences validées, donc le geste
 * change un chiffre que la personne concernée lira — il ne peut pas être
 * silencieux, et le motif part au journal du planning.
 */
function AnnulerAbsence({ a }: { a: RhAbsence }) {
  const { deciderAbsence } = useGed();
  const [ouvert, setOuvert] = React.useState(false);
  const [motif, setMotif] = React.useState('');

  if (!ouvert) {
    return (
      <Bouton taille="sm" variante="discret" onClick={() => setOuvert(true)}
        title="Absence saisie par erreur : l’annuler et recréditer le solde"
        style={{ marginInlineStart: 'auto' }}>
        Saisie par erreur…
      </Bouton>
    );
  }

  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <SuppressionRaisonnee
        titre="Annuler cette absence — saisie erronée"
        objet={<>{a.motifLabel} · du {a.dateDebut} au {a.dateFin}</>}
        nature="retrait"
        consequences={[
          ...(a.joursDecomptes
            ? [{
              cle: 'solde',
              texte: `${a.joursDecomptes} jour(s) seront RECRÉDITÉS au solde de congés.`,
              gravite: 'attention' as const,
              detail: 'Le compteur se dérive des absences validées : il changera dès l’enregistrement.',
            }]
            : []),
          {
            cle: 'planning',
            texte: 'La personne cesse d’apparaître absente sur cette période.',
            gravite: 'attention' as const,
          },
          {
            cle: 'trace',
            texte: 'La ligne reste à l’historique, avec son motif d’annulation.',
            gravite: 'info' as const,
            detail: 'Rien n’est effacé — l’acte s’inscrit au journal du planning.',
          },
        ]}
        note={(
          <div style={{ display: 'grid', gap: 6 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }} htmlFor={`annul-abs-${a.documentId}`}>
              Motif de l’annulation <span style={{ color: 'var(--red-600)' }}>*</span>
            </label>
            <textarea id={`annul-abs-${a.documentId}`} value={motif} rows={2}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Ex. saisie sur la mauvaise personne : l’absence concernait un homonyme."
              style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {motif.trim().length}/{MOTIF_DEROGATION_MIN} caractères minimum — relu en audit.
            </span>
          </div>
        )}
        libelleAction="Annuler l’absence"
        onConfirmer={async () => {
          if (motif.trim().length < MOTIF_DEROGATION_MIN) {
            return `Motif trop court : ${MOTIF_DEROGATION_MIN} caractères au minimum.`;
          }
          const ok = await deciderAbsence(a.documentId, 'annulee', motif.trim());
          return ok ? '' : 'L’annulation n’a pas abouti.';
        }}
        onAnnuler={() => { setOuvert(false); setMotif(''); }} />
    </div>
  );
}

function BoutonJustificatif({ a }: { a: RhAbsence }) {
  const { showToast } = useGed();
  const { demander, confirmation } = useConfirmation();
  const [encours, setEncours] = React.useState(false);

  const ouvrir = async () => {
    if (!(await demander(
      'Le justificatif d’une absence est une DONNÉE DE SANTÉ.\n\n'
      + 'Votre consultation sera enregistrée à votre nom dans le journal du laboratoire '
      + '(la personne concernée et les dates, jamais le motif ni le nom du fichier).\n\nOuvrir ?',
      { confirmer: 'Ouvrir' },
    ))) return;
    setEncours(true);
    try {
      const r = await fetch(`/api/ged/ged/rh/absences/${a.documentId}/justificatif`, { cache: 'no-store' });
      const d = await r.json().catch(() => null);
      if (!r.ok || !d?.data) {
        showToast(d?.error?.message || `Le justificatif n’a pas pu être ouvert (${r.status}).`, 'erreur');
        return;
      }
      const bin = atob(String(d.data));
      const buf = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) buf[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([buf]));
      const lien = document.createElement('a');
      lien.href = url; lien.download = String(d.nom || a.justificatifNom || 'justificatif');
      lien.click();
      URL.revokeObjectURL(url);
    } finally {
      setEncours(false);
    }
  };

  return (<>
    <Bouton taille="sm" onClick={() => void ouvrir()} disabled={encours} title={`${a.justificatifNom || 'justificatif'} — consultation enregistrée à votre nom`}>
      <I n="doc" s={11} /> {encours ? '…' : 'Justificatif'}
    </Bouton>
    {confirmation}
  </>);
}

/**
 * QUI D'AUTRE EST ABSENT SUR LA MÊME PÉRIODE (RQ, 27/08/2026).
 *
 * « Quand je lis la demande, je veux voir si d'autres personnes ont demandé ou
 * ont déjà une plage concomitante validée — d'autant plus si elles ont la même
 * fonction ou sont suppléantes l'une de l'autre. »
 *
 * ⚠️ ÇA AVERTIT, ÇA NE BLOQUE PAS (décision RQ du 27/08). L'outil ne connaît ni
 * la charge réelle du jour, ni qui peut couvrir qui en pratique. Il nomme ce
 * qu'il sait — la fonction, la suppléance, le nombre de jours communs — et la
 * décision reste entière.
 *
 * ⚠️ RIEN NE S'AFFICHE QUAND IL N'Y A RIEN. Un bandeau « aucun conflit » à
 * chaque demande deviendrait du décor, et le jour où il dirait autre chose,
 * personne ne le lirait. Le calcul, lui, est fait dans tous les cas : c'est
 * l'absence de bandeau qui porte l'information.
 */
function Concomitances({ a }: { a: RhAbsence }) {
  const liste = a.concomitantes || [];
  if (!liste.length) return null;
  const sensible = !!a.concomitanceSensible;
  return (
    <div style={{
      borderTop: '1px solid var(--border-subtle)', paddingTop: 9,
      display: 'grid', gap: 5,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: sensible ? 'var(--red-600)' : 'var(--text-muted)' }}>
        <I n={sensible ? 'alert-triangle' : 'info'} s={13} />
        {liste.length === 1 ? 'Une autre absence' : `${liste.length} autres absences`} sur la période
      </div>
      {liste.map((c) => (
        <div key={c.documentId} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12.5, flexWrap: 'wrap' }}>
          <strong>{c.nom}</strong>
          <span style={{ color: 'var(--text-muted)' }}>
            {c.dateDebut === c.dateFin ? c.dateDebut : `${c.dateDebut} → ${c.dateFin}`}
            {' · '}{c.joursCommuns} jour{c.joursCommuns > 1 ? 's' : ''} en commun
          </span>
          {/* Le statut se DIT : « déjà validée » et « demandée » n'engagent pas
              la même chose — la seconde peut encore être refusée. */}
          <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '1px 8px', background: c.statut === 'validee' ? 'var(--gray-25)' : 'var(--amber-50)', color: c.statut === 'validee' ? 'var(--text-muted)' : 'var(--amber-600)' }}>
            {c.statut === 'validee' ? 'déjà validée' : 'demandée'}
          </span>
          {c.suppleanceSur && (
            <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '1px 8px', background: 'var(--red-50)', color: 'var(--red-600)' }}>
              votre suppléant sur {c.suppleanceSur}
            </span>
          )}
          {c.memeFonction && (
            <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '1px 8px', background: 'var(--red-50)', color: 'var(--red-600)' }}>
              même fonction
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function LigneAbsence({ a }: { a: RhAbsence }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13.5 }}>{nomDe(a.personnel) || '—'}</strong>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{a.motifLabel}</span>
        <Statut s={a.statut} />
        {a.aJustificatif && <BoutonJustificatif a={a} />}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
        du {fmt(a.dateDebut)} au {fmt(a.dateFin)}
        {a.joursDecomptes != null && <> · {a.joursDecomptes} j</>}
        {a.decompteSolde && <> · décompté du solde</>}
      </div>
      {a.commentaire && <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 3 }}>{a.commentaire}</div>}
      {a.statut === 'refusee' && a.motifDecision && (
        <div style={{ fontSize: 12.5, color: 'var(--red-600)', marginTop: 3 }}>Refus : {a.motifDecision}</div>
      )}
    </div>
  );
}

function FormulaireSaisie({ personnel, motifs, onFermer, onValider }: {
  personnel: RhPersonnel[];
  motifs: { code: string; label: string; justificatifRequis: boolean; sensible: boolean; decompteSolde?: boolean; dureeDefautJours?: number | null }[];
  onFermer: () => void;
  onValider: (d: { motif: string; personnelOrgId: string; dateDebut: string; dateFin: string; commentaire?: string; justificatifNom?: string; justificatifData?: string; remplacantOrgId?: string; sansRemplacant?: boolean; justificatifType?: string; adressePendant?: string; telephonePendant?: string; contactUrgence?: string; telephoneUrgence?: string }) => Promise<void>;
}) {
  const [qui, setQui] = React.useState(personnel[0]?.orgId || '');
  const [motif, setMotif] = React.useState(motifs[0]?.code || '');
  const [debut, setDebut] = React.useState('');
  const [fin, setFin] = React.useState('');
  /* ⚠️ La fin a-t-elle été SAISIE À LA MAIN ? Une date proposée se remplace ;
     une date choisie, jamais. Sans ce drapeau, corriger la fin la verrait
     revenir au premier changement de motif ou de date de début. */
  const [finSaisie, setFinSaisie] = React.useState(false);
  const [commentaire, setCommentaire] = React.useState('');
  /* Les champs du formulaire ENR-H1-01 (RQ, 29/08/2026). Ils n'existaient pas :
     la demande se faisait sans remplaçant, sans coordonnées et sans contact
     d'urgence, alors que le formulaire papier les porte tous les trois. */
  const [remplacant, setRemplacant] = React.useState('');
  /* « Personne n'a remplacé » (29/09/2026) — régularisation d'un congé déjà
     pris, poste resté vide. Déclaré, jamais déduit d'un champ laissé vide. */
  const [sansRemplacant, setSansRemplacant] = React.useState(false);
  const [justifType, setJustifType] = React.useState('');
  const [adresse, setAdresse] = React.useState('');
  const [telPendant, setTelPendant] = React.useState('');
  const [contactUrg, setContactUrg] = React.useState('');
  const [telUrg, setTelUrg] = React.useState('');
  const [fichier, setFichier] = React.useState<{ nom: string; data: string } | null>(null);
  // Refus du justificatif, dit SOUS le champ : le serveur borne aussi (8 Mo,
  // image ou PDF, contenu réel), mais un refus qui n'arrive qu'après l'envoi
  // ferait attendre pour rien — et ne nommait ni la limite ni le poids.
  const [refusFichier, setRefusFichier] = React.useState<string | null>(null);
  useEscape(true, onFermer);
  const m = motifs.find((x) => x.code === motif);

  /* ── LA DURÉE PROPOSÉE (RQ, 03/09/2026) ──────────────────────────────────
   * « Le congé maternité s'affiche par défaut à [sa durée] mais peut être
   * prolongé. » La fin est donc PRÉ-REMPLIE, jamais imposée : le champ reste
   * ouvert, et le serveur ne refuse rien — `dureeDefautJours` propose,
   * `dureeMax` seul bornerait, et la maternité n'en a pas.
   *
   * ⚠️ Bornes INCLUSES : cent cinq jours à partir du 3 juillet finissent le
   * 15 octobre, pas le 16.
   *
   * ⚠️ DÉRIVÉE, PAS POSÉE PAR UN EFFET. Une première version appelait `setFin`
   * dans un `useEffect` : la règle `react-hooks` la refuse, et elle a raison —
   * un état posé depuis un effet déclenche un second rendu et fait clignoter le
   * champ. On CALCULE la valeur affichée, et la saisie manuelle la fige. */
  const finProposee = React.useMemo(() => {
    const j = m?.dureeDefautJours;
    if (!j || j < 1 || !debut) return '';
    const d = new Date(`${debut}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return '';
    d.setUTCDate(d.getUTCDate() + Math.floor(j) - 1);
    return isoJour(d);
  }, [debut, m?.dureeDefautJours]);
  const finEffective = finSaisie || fin ? fin : finProposee;

  const lire = async (f: File | null, champ: HTMLInputElement | null) => {
    if (!f) { setFichier(null); setRefusFichier(null); return; }
    const refus = await refusDepot('pieceRh', f, 'Le justificatif');
    if (refus) {
      setFichier(null); setRefusFichier(refus);
      if (champ) champ.value = '';
      return;
    }
    const r = new FileReader();
    r.onload = () => { setRefusFichier(null); setFichier({ nom: f.name, data: String(r.result || '').split(',').pop() || '' }); };
    r.readAsDataURL(f);
  };

  return (
    <Modale titre="Enregistrer une absence" onFermer={onFermer}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={lbl}>Salarié
          <Selecteur style={{ ...inputStyle, width: '100%' }} value={qui} onChange={setQui}>
            {personnel.map((p) => <option key={p.orgId} value={p.orgId}>{nomDe(p)}</option>)}
          </Selecteur>
        </label>
        <label style={lbl}>Motif
          <select style={{ ...inputStyle, width: '100%' }} value={motif} onChange={(e) => setMotif(e.target.value)}>
            {motifs.map((x) => <option key={x.code} value={x.code}>{x.label}</option>)}
          </select>
        </label>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
        <label style={lbl}>Du
          <input type="date" style={{ ...inputStyle, width: '100%' }} value={debut}
            onChange={(e) => {
              setDebut(e.target.value);
              /* Le repli « fin = début » ne vaut que si le motif ne PROPOSE
                 rien : sinon il ferait clignoter une absence d'un jour avant
                 que la durée proposée ne s'applique. */
              if (!fin && !m?.dureeDefautJours) setFin(e.target.value);
            }} />
        </label>
        <label style={lbl}>Au (dernier jour d’absence, inclus)
          {/* ⚠️ Toucher la fin la fige : la durée proposée ne la reprendra plus. */}
          <input type="date" style={{ ...inputStyle, width: '100%' }} value={finEffective}
            onChange={(e) => { setFin(e.target.value); setFinSaisie(true); }} />
        </label>
      </div>
      <RepriseConge debut={debut} fin={finEffective} />

      {m?.sensible && (
        <p style={{ fontSize: 12.5, color: 'var(--amber-600)', background: 'var(--amber-50)', borderRadius: 'var(--radius-sm)', padding: '7px 9px', marginTop: 10 }}>
          Donnée de santé : le motif, le commentaire et le certificat ne seront lisibles que par vous
          et par la personne concernée. Le journal d’audit n’en portera pas le motif.
        </p>
      )}

      <label style={{ ...lbl, marginTop: 10 }}>
        Justificatif {m?.justificatifRequis && <span style={{ color: 'var(--text-muted)' }}>(attendu pour ce motif)</span>}
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif" style={{ ...inputStyle, width: '100%', padding: 6 }}
          onChange={(e) => void lire(e.target.files?.[0] || null, e.target)} />
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 400 }}>PDF ou image, 8 Mo maximum.</span>
      </label>
      {refusFichier && <p role="alert" style={{ fontSize: 12.5, color: 'var(--red-600)', margin: '4px 0 0' }}>{refusFichier}</p>}
      {fichier && <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Joint : {fichier.nom}</p>}


      {/* ⚠️ LE REMPLAÇANT — « Obligatoire » sur le formulaire ENR-H1-01, et absent
          de l'outil jusqu'au 29/08/2026. Sans lui, une absence est accordée sans
          que personne sache qui tient le poste : c'est le trou que la demande
          existe pour éviter. Le serveur le refuse pour un congé décompté. */}
      <label style={{ ...lbl, marginTop: 10 }}>
        Remplaçant {m?.decompteSolde !== false && <span style={{ color: 'var(--red-600)' }}>(obligatoire)</span>}
        <select style={{ ...inputStyle, width: '100%' }} value={remplacant} disabled={sansRemplacant}
          onChange={(e) => setRemplacant(e.target.value)}>
          <option value="">— choisir —</option>
          {personnel
            .filter((p) => p.statut !== 'sorti' && p.orgId !== qui)
            .map((p) => <option key={p.orgId} value={p.orgId}>{nomDe(p)}</option>)}
        </select>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 400 }}>
          Son visa se donne depuis SA session — il n’est pas signé ici, sinon ce ne serait pas un visa.
        </span>
      </label>
      {m?.decompteSolde !== false && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, marginTop: 6 }}>
          <input type="checkbox" checked={sansRemplacant}
            onChange={(e) => { setSansRemplacant(e.target.checked); if (e.target.checked) setRemplacant(''); }} />
          Personne n’a remplacé (congé déjà pris, poste resté vide) — noté au journal
        </label>
      )}

      <label style={{ ...lbl, marginTop: 10 }}>Type de justificatif
        <select style={{ ...inputStyle, width: '100%' }} value={justifType}
          onChange={(e) => setJustifType(e.target.value)}>
          <option value="">— aucun —</option>
          <option value="certificat">Certificat médical</option>
          <option value="attestation">Attestation</option>
          <option value="autre">Autre</option>
        </select>
      </label>

      {/* Coordonnées pendant le congé — le formulaire les demande pour pouvoir
          joindre la personne, pas pour meubler. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
        <label style={lbl}>Adresse pendant le congé
          <input style={{ ...inputStyle, width: '100%' }} value={adresse} onChange={(e) => setAdresse(e.target.value)} />
        </label>
        <label style={lbl}>Téléphone pendant le congé
          <input style={{ ...inputStyle, width: '100%' }} value={telPendant} onChange={(e) => setTelPendant(e.target.value)} />
        </label>
        <label style={lbl}>Personne à contacter en cas d’urgence
          <input style={{ ...inputStyle, width: '100%' }} value={contactUrg} onChange={(e) => setContactUrg(e.target.value)} />
        </label>
        <label style={lbl}>Son téléphone
          <input style={{ ...inputStyle, width: '100%' }} value={telUrg} onChange={(e) => setTelUrg(e.target.value)} />
        </label>
      </div>

      <label style={{ ...lbl, marginTop: 10 }}>Commentaire
        <textarea rows={2} style={{ ...inputStyle, width: '100%' }} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
      </label>

      <Actions onFermer={onFermer} valide={!!qui && !!motif && !!debut && !!finEffective && !(fin < debut)}
        libelle="Enregistrer"
        onValider={() => onValider({
          motif, personnelOrgId: qui, dateDebut: debut, dateFin: finEffective, commentaire,
          remplacantOrgId: remplacant, sansRemplacant, justificatifType: justifType,
          adressePendant: adresse, telephonePendant: telPendant,
          contactUrgence: contactUrg, telephoneUrgence: telUrg,
          ...(fichier ? { justificatifNom: fichier.nom, justificatifData: fichier.data } : {}),
        })} />
    </Modale>
  );
}

function FormulaireRefus({ a, onFermer, onValider }: {
  a: RhAbsence; onFermer: () => void; onValider: (motif: string) => Promise<void>;
}) {
  const [m, setM] = React.useState('');
  useEscape(true, onFermer);
  return (
    <Modale titre={`Refuser la demande de ${nomDe(a.personnel)}`} onFermer={onFermer}>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        {a.motifLabel} · du {fmt(a.dateDebut)} au {fmt(a.dateFin)}
      </p>
      <label style={{ ...lbl, marginTop: 10 }}>
        Motif du refus <Help aide="Le motif est communiqué au demandeur. Un refus sans explication se transforme en conflit — et le serveur l’exige.">obligatoire</Help>
        <textarea rows={3} style={{ ...inputStyle, width: '100%' }} value={m} onChange={(e) => setM(e.target.value)}
          placeholder="Ex. : effectif insuffisant sur la période, deux autres absences déjà validées." />
      </label>
      <Actions onFermer={onFermer} valide={m.trim().length >= 5} libelle="Refuser la demande" onValider={() => onValider(m.trim())} />
    </Modale>
  );
}

/* ---- éléments partagés ---- */

/** Depuis le socle UX du 11/08/2026, la modale RH est un HABILLAGE de la
 *  modale commune (`ui.tsx`) : Échap, clic sur le fond, hauteur bornée avec
 *  CORPS seul défilant — le contrat unique, au lieu d'un seizième overlay.
 *  La signature historique (`onFermer`) est conservée : six écrans l'appellent. */
export function Modale({ titre, sousTitre, onFermer, children }: {
  titre: string; sousTitre?: React.ReactNode; onFermer: () => void; children: React.ReactNode;
}) {
  return (
    <ModaleCommune titre={titre} sousTitre={sousTitre} onClose={onFermer}>
      {children}
    </ModaleCommune>
  );
}

function Actions({ onFermer, onValider, valide, libelle }: {
  onFermer: () => void; onValider: () => Promise<void> | void; valide: boolean; libelle: string;
}) {
  const [envoi, setEnvoi] = React.useState(false);
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'end', marginTop: 16 }}>
      <Bouton onClick={onFermer} disabled={envoi}>Annuler</Bouton>
      <Bouton variante="primaire" disabled={!valide || envoi} onClick={async () => { setEnvoi(true); try { await onValider(); } finally { setEnvoi(false); } }}>
        {envoi ? 'En cours…' : libelle}
      </Bouton>
    </div>
  );
}

export const lbl: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
  fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-body)',
};
const h2: React.CSSProperties = { fontFamily: 'var(--font-display)', fontSize: 17, margin: '22px 0 8px' };
const th: React.CSSProperties = {
  fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '.04em', color: 'var(--text-muted)', padding: '9px 12px',
  borderBottom: '1px solid var(--border-default)', background: 'var(--gray-25)', textAlign: 'end',
};
const td: React.CSSProperties = {
  padding: '9px 12px', borderBottom: '1px solid var(--border-subtle)',
  fontFamily: 'var(--font-sans)', fontSize: 12.5, textAlign: 'end',
};

export function AccesRefuseRh() {
  const rhAcces = useGed((s) => s.rhAcces);
  const rhAccesErreur = useGed((s) => s.rhAccesErreur);
  const loadRhAcces = useGed((s) => s.loadRhAcces);
  if (rhAccesErreur) {
    return (
      <EmptyState
        titre="Impossible de vérifier l’accès RH"
        indice="Panne réseau ou serveur — ce n’est pas un refus d’accès, réessayez."
        action={<Bouton taille="sm" onClick={() => loadRhAcces()}>Réessayer</Bouton>}
      />
    );
  }
  return (
    <EmptyState
      titre={rhAcces && !rhAcces.forfait ? 'Module RH non inclus dans le forfait' : 'Réservé au gestionnaire RH'}
      indice={rhAcces && !rhAcces.forfait
        ? 'Ce module ne fait pas partie de l’abonnement du laboratoire. Le RQ ne peut pas le débloquer — cela relève du contrat.'
        : 'Le rôle « Gestionnaire RH / paie » s’attribue depuis l’écran Organigramme. Être RQ n’y donne pas droit : l’accès suit la fonction, pas la personne.'}
    />
  );
}
